import React from 'react';
import axios from 'axios';

// Конфигурация axios с оптимизациями
const axiosConfig = {
  timeout: 10000, // 10 секунд таймаут
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'max-age=300', // 5 минут кэш
  },
};

// Создаем экземпляр axios с конфигурацией
const apiClient = axios.create(axiosConfig);

// Кэш для HTTP запросов
const httpCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 минут

// Функция для кэширования HTTP запросов
const cachedRequest = async (url, options = {}) => {
  const cacheKey = `${url}_${JSON.stringify(options)}`;
  const cached = httpCache.get(cacheKey);
  
  if (cached && (Date.now() - cached.timestamp) < CACHE_TTL) {
    return cached.data;
  }
  
  try {
    const response = await apiClient(url, options);
    httpCache.set(cacheKey, {
      data: response,
      timestamp: Date.now()
    });
    return response;
  } catch (error) {
    // Если ошибка, возвращаем кэшированные данные если есть
    if (cached) {
      return cached.data;
    }
    throw error;
  }
};

// Функция для кэширования в localStorage с TTL
const cacheWithTTL = (key, data, ttl = 5 * 60 * 1000) => {
  try {
    const cacheData = {
      data,
      timestamp: Date.now(),
      ttl
    };
    localStorage.setItem(key, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Cache write error:', error);
  }
};

// Функция для получения данных из кэша localStorage
const getCachedData = (key) => {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    
    const cacheData = JSON.parse(cached);
    const now = Date.now();
    
    if (now - cacheData.timestamp > cacheData.ttl) {
      localStorage.removeItem(key);
      return null;
    }
    
    return cacheData.data;
  } catch (error) {
    console.error('Cache read error:', error);
    return null;
  }
};

// Функция для очистки устаревших кэшей
const cleanupExpiredCaches = () => {
  try {
    const keys = Object.keys(localStorage);
    const now = Date.now();
    
    keys.forEach(key => {
      if (key.includes('_cache_') || key.includes('Snapshot')) {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const cacheData = JSON.parse(cached);
            if (cacheData.timestamp && (now - cacheData.timestamp) > (cacheData.ttl || 5 * 60 * 1000)) {
              localStorage.removeItem(key);
            }
          }
        } catch (error) {
          // Удаляем поврежденные кэши
          localStorage.removeItem(key);
        }
      }
    });
  } catch (error) {
    console.error('Cache cleanup error:', error);
  }
};

// Очищаем кэши при загрузке приложения
cleanupExpiredCaches();

// Дебаунсинг функция
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Хук для дебаунсинга
export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = React.useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Экспортируем функции
export {
  apiClient,
  cachedRequest,
  cacheWithTTL,
  getCachedData,
  cleanupExpiredCaches,
  debounce
};

// Дефолтный экспорт для обратной совместимости
export default apiClient;
